import { useCallback } from 'react'

import type { SelectedCategory } from '../../../types'
import { getDirectParentFromBreadcrumbs } from '../../../utils/breadcrumbs'

type UseCategoryNavigationProps = {
  selectedCategory: SelectedCategory
  parentCategory: SelectedCategory
  onSelect: (category: SelectedCategory) => void
}

const createCategoryFromParent = (category: NonNullable<SelectedCategory>): SelectedCategory => ({
  id: category.parentId!,
  name: getDirectParentFromBreadcrumbs(category.breadcrumbs) ?? '',
  isVertical: false,
  breadcrumbs: category.breadcrumbs.slice(0, -1),
  children: category.children,
  allowSelection: category.allowSelection,
})

export const useCategoryNavigation = ({
  selectedCategory,
  parentCategory,
  onSelect,
}: UseCategoryNavigationProps) => {
  const navigateUp = useCallback(() => {
    if (!selectedCategory) return

    if (!selectedCategory.parentId) {
      onSelect(undefined)

      return
    }

    if (selectedCategory.children.length > 0) {
      onSelect(createCategoryFromParent(selectedCategory))

      return
    }

    if (parentCategory?.parentId) {
      onSelect(createCategoryFromParent(parentCategory))

      return
    }

    onSelect(undefined)
  }, [selectedCategory, parentCategory, onSelect])

  const navigateDown = useCallback(
    (category: SelectedCategory) => {
      onSelect(category)
    },
    [onSelect],
  )

  return {
    navigateUp,
    navigateDown,
  }
}
